import { IGif } from '@giphy/js-types'
import React, { useContext, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { IChannel } from 'shared/types/channel'
import ModalContext from '../context/modal'
import UserContext from '../context/user'
import { Modal } from '../modal'
import AddGifsToCollection from './add-gifs-to-collection'
import CollectionEditor from './editor'
import Header from './header'
import { Container, GifSelectorContainer } from './sc'
import { CollectionContext } from 'shared/components/context/add-to-collections-manager'
import { addGifs } from 'shared/api/channels'
import { fitGifToSize } from 'shared/util/sizing'
import CollectionsCabinet from './collections-cabinet'

export const GIF_WIDTH = 500
export const GIF_HEIGHT = 573
export const MIN_HEIGHT = 380
const MODAL_TOP = 193
const MODAL_MARGIN_LEFT = -12
const MODAL_HEIGHT = 630
const MODAL_WIDTH = 988

const Overlay = styled.div`
    cursor: pointer;
    height: 100vh;
    left: 0;
    position: fixed;
    top: 0;
    width: 100vw;
    z-index: -1;
`

type ModalProps = {
    onClose: () => void
}

export type NewCollectionProps = {
    userId: number
    parentChannel: IChannel
    gif?: IGif
    redirectOnCreate?: boolean
}

export const NewCollectionModal = ({
    onClose,
    newCollectionProps,
}: ModalProps & {
    newCollectionProps: NewCollectionProps
}) => {
    const { user } = useContext(UserContext)

    const isRootCollection = user.channelId === newCollectionProps.parentChannel.id

    return (
        <Modal>
            <Container top={190}>
                <Header
                    title={isRootCollection ? 'New Collection' : 'New Sub-Collection'}
                    onClose={() => {
                        onClose()
                    }}
                />
                <CollectionEditor newCollectionProps={newCollectionProps} onComplete={onClose} />
            </Container>
        </Modal>
    )
}

export const EditCollectionModal = ({
    onClose,
    channel,
}: ModalProps & {
    channel: IChannel
}) => {
    return (
        <Modal>
            <Container height={680}>
                <Header title="Edit Collection" onClose={onClose} />
                <CollectionEditor channel={channel} onComplete={onClose} />
            </Container>
        </Modal>
    )
}

export const ChannelGifSelector = ({
    onClose,
    channelId,
    searchChannelId,
}: ModalProps & {
    channelId: number
    searchChannelId: number
}) => {
    const { channels, setAddGifsToCollectionSuccessMessage } = useContext(CollectionContext)

    const handleComplete = addedGifs => {
        if (channels[channelId]) {
            setAddGifsToCollectionSuccessMessage({
                gifs: addedGifs,
                channelId,
                channelName: channels[channelId].display_name,
            })
        }
        onClose()
    }

    return (
        <Modal>
            <GifSelectorContainer>
                <Header title="Your Channel" onClose={onClose} />
                <AddGifsToCollection
                    channelId={channelId}
                    onComplete={handleComplete}
                    searchChannelId={searchChannelId}
                />
            </GifSelectorContainer>
        </Modal>
    )
}

// This is the Collections Cabinet
type GifSize = { height: number }
type CabinetProps = { channelId?: number } & ModalProps
export const CollectionCabinetModal = ({ onClose, channelId }: CabinetProps) => {
    const {
        channels,
        setActiveChannelId,
        gif,
        setGif,
        setAddGifToCollectionSuccessMessage,
        rootChannelId,
    } = useContext(CollectionContext)
    const { openAddGifsToCollectionPanel } = useContext(ModalContext)

    let gifSize: GifSize = {
        height: MIN_HEIGHT,
    }

    if (gif) {
        gifSize = fitGifToSize(gif, GIF_WIDTH, GIF_HEIGHT) as GifSize
    }

    // Reset variables before close the modal
    const handleClose = () => {
        setActiveChannelId(rootChannelId)
        setGif(undefined)
        onClose()
    }

    const handleAdd = async (channelId: number) => {
        if (gif) {
            await addGifs([
                {
                    gif: gif.id,
                    channel: channelId,
                },
            ])
            const channel = channels[channelId]
            setAddGifToCollectionSuccessMessage({
                channelName: channel.display_name,
                featuredGif: channel.featured_gif || gif,
                channelId: channel.id,
            })
            handleClose()
        }
    }

    const handleClickChannel = async (channelId: number) => {
        if (gif && !channels[channelId].has_children) {
            handleAdd(channelId)
        } else {
            setActiveChannelId(channelId)
        }
    }

    const handleAddTo = (channelId: number, searchChannelId?: number) => {
        if (gif) {
            handleAdd(channelId)
        } else if (searchChannelId) {
            openAddGifsToCollectionPanel(channelId, searchChannelId)
        }
    }

    const modalHeight = useMemo(() => {
        if (gif && gif.type === 'gif') {
            return gifSize?.height > MIN_HEIGHT ? gifSize.height : MIN_HEIGHT
        }

        if (!gif) {
            return MODAL_HEIGHT
        }
    }, [gif])

    const modalTop = useMemo(() => {
        if (gif && gif.type === 'gif') {
            return MODAL_TOP
        }
    }, [gif])

    const modalMarginLeft = useMemo(() => {
        if (gif && gif.type === 'gif') {
            return MODAL_MARGIN_LEFT
        }
    }, [gif])

    const modalWidth = useMemo(() => {
        if (!gif) {
            return MODAL_WIDTH
        }
    }, [gif])

    useEffect(() => {
        // disable body scrolling
        document.body.style.overflow = 'hidden'

        return () => {
            setActiveChannelId(rootChannelId)
            setGif(undefined)

            // re-enable body scrolling
            document.body.style.overflow = ''
        }
    }, [])

    return (
        <Modal>
            <Container top={modalTop} height={modalHeight} marginLeft={modalMarginLeft} width={modalWidth}>
                <CollectionsCabinet
                    onClose={handleClose}
                    channelId={channelId}
                    onClickChannel={handleClickChannel}
                    onClickAddTo={handleAddTo}
                />
            </Container>
            <Overlay onClick={handleClose} />
        </Modal>
    )
}
